import React, { useState, useEffect } from 'react';
import {
  Card,
  Upload,
  Input,
  Button,
  Space,
  message,
  Row,
  Col,
  Typography,
  Spin,
  Empty,
  Tag,
  Image,
} from 'antd';
import {
  SearchOutlined,
  UploadOutlined,
  LinkOutlined,
  CopyOutlined,
  EyeOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import {
  searchSimilarImagesByFile,
  searchSimilarImagesByUrl,
  SimilarImageResult,
} from '../../services/imageSearch';
import Img from '../../components/Img';
import eventBus from '../../utils/eventBus';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ImageSearch: React.FC = () => {
  const { t } = useTranslation();
  const [searchMode, setSearchMode] = useState<'file' | 'url'>('file');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SimilarImageResult[]>([]);
  const [maxResults, setMaxResults] = useState(10);
  const [maxDistance, setMaxDistance] = useState(10);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null); // null 表示正在检查
  const [enableImageSearch, setEnableImageSearch] = useState<boolean>(false);

  // 检查登录状态和图搜功能是否启用
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await fetch('/login/info', {
          method: 'GET',
          credentials: 'include',
        });
        const result = await response.json();
        
        // 检查是否登录成功
        if (result.isSuccess && (result.email || result.detail?.username)) {
          setIsLoggedIn(true);
          
          // 检查用户是否启用了图搜功能
          try {
            const config = typeof result.detail?.config === 'string' 
              ? JSON.parse(result.detail.config || '{}') 
              : result.detail?.config || {};
            setEnableImageSearch(config.enableImageSearch === true);
            
            // 如果未启用图搜功能，提示用户
            if (!config.enableImageSearch) {
              message.warning(t('imageSearchPage.notEnabled'));
            }
          } catch (err) {
            console.error('解析用户配置失败:', err);
            setEnableImageSearch(false);
          }
        } else {
          setIsLoggedIn(false);
          setEnableImageSearch(false);
        }
      } catch (error) {
        console.error('检查登录状态失败:', error);
        setIsLoggedIn(false);
        setEnableImageSearch(false);
      }
    };
    
    checkLoginStatus();
  }, []);

  // 组件卸载时释放预览URL，防止内存泄漏
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // 处理文件选择
  const handleFileChange = (info: any) => {
    console.log('handleFileChange called:', info);
    
    // 释放旧的预览URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    
    // 当 beforeUpload 返回 false 时，文件不会上传，但 onChange 仍会触发
    // 此时 info.file 就是 File 对象，或者通过 originFileObj 获取
    const file = info.file.originFileObj || info.file;
    
    console.log('Extracted file:', file, 'is File:', file instanceof File);
    
    // 确保是 File 对象
    if (file instanceof File) {
      setUploadedFile(file);
      // 创建新的预览URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      console.log('File selected successfully:', file.name);
    } else {
      console.warn('选择的不是有效的文件对象:', file);
      message.warning(t('imageSearchPage.fileSelectFailed'));
    }
  };

  // 执行搜索
  const handleSearch = async () => {
    // 检查登录状态
    if (!isLoggedIn) {
      message.warning(t('imageSearchPage.loginFirst'));
      eventBus.emit('openLoginModal');
      return;
    }
    
    if (searchMode === 'file' && !uploadedFile) {
      message.warning(t('imageSearchPage.uploadFirst'));
      return;
    }
    if (searchMode === 'url' && !imageUrl.trim()) {
      message.warning(t('imageSearchPage.enterUrl'));
      return;
    }

    setLoading(true);
    setResults([]);

    try {
      let searchResults: SimilarImageResult[];
      if (searchMode === 'file' && uploadedFile) {
        searchResults = await searchSimilarImagesByFile(
          uploadedFile,
          maxResults,
          maxDistance
        );
      } else {
        searchResults = await searchSimilarImagesByUrl(
          imageUrl.trim(),
          maxResults,
          maxDistance
        );
      }

      setResults(searchResults);
      if (searchResults.length === 0) {
        message.info(t('imageSearchPage.noSimilarFound'));
      } else {
        message.success(t('imageSearchPage.foundSimilar', { count: searchResults.length }));
      }
    } catch (error: any) {
      message.error(error.message || t('imageSearchPage.searchFailed'));
      console.error('搜索失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 复制链接
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success(t('imageSearchPage.linkCopied'));
  };

  // 格式化相似度显示
  const formatSimilarity = (score: number) => {
    if (score >= 90) return { text: t('imageSearchPage.verySimilar'), color: 'success' };
    if (score >= 70) return { text: t('imageSearchPage.similar'), color: 'processing' };
    if (score >= 50) return { text: t('imageSearchPage.somewhatSimilar'), color: 'warning' };
    return { text: t('imageSearchPage.general'), color: 'default' };
  };

  return (
    <div className="image-search-page" style={{ padding: '24px' }}>
      <Title level={2}>{t('imageSearchPage.title')}</Title>
      <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
        {t('imageSearchPage.subtitle')}
      </Text>

      {/* 搜索输入区域 */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* 搜索模式切换 */}
          <Space>
            <Button
              type={searchMode === 'file' ? 'primary' : 'default'}
              icon={<UploadOutlined />}
              onClick={() => {
                // 切换模式时释放预览URL
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                }
                setSearchMode('file');
              }}
            >
              上传图片
            </Button>
            <Button
              type={searchMode === 'url' ? 'primary' : 'default'}
              icon={<LinkOutlined />}
              onClick={() => {
                // 切换模式时释放预览URL
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                }
                setSearchMode('url');
              }}
            >
              输入URL
            </Button>
          </Space>

          {/* 文件上传 */}
          {searchMode === 'file' && (
            <div>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleFileChange}
                disabled={isLoggedIn === false}
              >
                <Button 
                  icon={<UploadOutlined />}
                  disabled={isLoggedIn === false}
                >
                  {isLoggedIn === false ? t('imageSearchPage.pleaseLogin') : t('imageSearchPage.selectImageFile')}
                </Button>
              </Upload>
              {previewUrl && (
                <div style={{ marginTop: '16px' }}>
                  <Text strong>{t('imageSearchPage.preview')}</Text>
                  <div style={{ marginTop: '8px' }}>
                    <img
                      src={previewUrl}
                      alt={t('imageSearchPage.preview')}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* URL输入 */}
          {searchMode === 'url' && (
            <div>
              <TextArea
                placeholder={isLoggedIn ? t('imageSearchPage.placeholderUrl') : t('imageSearchPage.placeholderLogin')}
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                rows={3}
                style={{ marginBottom: '16px' }}
                disabled={!isLoggedIn}
              />
              {imageUrl && (
                <div style={{ marginTop: '8px' }}>
                  <Text strong>{t('imageSearchPage.preview')}</Text>
                  <div style={{ marginTop: '8px' }}>
                    <img
                      src={imageUrl}
                      alt={t('imageSearchPage.preview')}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                      }}
                      onError={() => {
                        message.warning(t('imageSearchPage.invalidPreviewUrl'));
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 搜索参数 */}
          <Space>
            <Text>{t('imageSearchPage.maxResults')}</Text>
            <Input
              type="number"
              value={maxResults}
              onChange={(e) => setMaxResults(Number(e.target.value) || 10)}
              style={{ width: '80px' }}
              min={1}
              max={50}
              disabled={!isLoggedIn}
            />
            <Text>{t('imageSearchPage.maxDistance')}</Text>
            <Input
              type="number"
              value={maxDistance}
              onChange={(e) => setMaxDistance(Number(e.target.value) || 10)}
              style={{ width: '80px' }}
              min={0}
              max={64}
              disabled={!isLoggedIn}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              (距离越小越相似，建议值：0-10)
            </Text>
          </Space>

          {/* 搜索按钮 */}
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            disabled={!isLoggedIn}
            size="large"
            block
          >
            {isLoggedIn === false ? t('imageSearchPage.pleaseLogin') : t('imageSearchPage.startSearch')}
          </Button>
          
          {/* 未登录提示 */}
          {isLoggedIn === false && (
            <div style={{ 
              textAlign: 'center', 
              padding: '12px', 
              background: '#fff7e6', 
              borderRadius: '4px',
              border: '1px solid #ffe58f'
            }}>
              <Text type="warning" style={{ fontSize: '13px' }}>
                {t('imageSearchPage.loginRequiredHint')}
              </Text>
              <Button
                type="link"
                size="small"
                icon={<LoginOutlined />}
                onClick={() => eventBus.emit('openLoginModal')}
                style={{ padding: '0 4px', height: 'auto' }}
              >
                {t('login.login')}
              </Button>
            </div>
          )}
        </Space>
      </Card>

      {/* 搜索结果 */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>{t('imageSearchPage.searching')}</Text>
          </div>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div>
          <Title level={4}>{t('imageSearchPage.searchResults', { count: results.length })}</Title>
          <Row gutter={[16, 16]}>
            {results.map((item, index) => {
              const similarity = formatSimilarity(item.similarityScore);
              return (
                <Col xs={24} sm={12} md={8} lg={6} key={item.record.id || index}>
                  <Card
                    hoverable
                    cover={
                      <div style={{ height: '200px', overflow: 'hidden', background: '#f5f5f5' }}>
                        <Img
                          src={item.record.url}
                          alt={item.record.fileName}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                          showPreview={true}
                        />
                      </div>
                    }
                    actions={[
                      <EyeOutlined
                        key="view"
                        onClick={() => window.open(item.record.url, '_blank')}
                      />,
                      <CopyOutlined
                        key="copy"
                        onClick={() => handleCopyUrl(item.record.url)}
                      />,
                    ]}
                  >
                    <Card.Meta
                      title={
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.record.fileName}
                        </div>
                      }
                      description={
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Space>
                            <Tag color={similarity.color}>
                              相似度: {item.similarityScore.toFixed(1)}%
                            </Tag>
                            <Tag>{t('imageSearchPage.distance', { value: item.hammingDistance })}</Tag>
                          </Space>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {item.record.size} · {new Date(item.record.time).toLocaleString()}
                          </Text>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {!loading && results.length === 0 && (
        <Empty description={t('imageSearchPage.noResults')} />
      )}
    </div>
  );
};

export default ImageSearch;
